import React from 'react';
import { IStyles, useClasses } from 'app/utils/css';
import { GREY_B } from 'app/constants/Colors';

export const enum InputVariant {
  Base = 'base',
  Wide = 'wide',
  Borderless = 'borderless',
}

// INPUT_STYLE without the border and outline/:focus styles
//
// Use this when you don't want to override the browser's default :focus state styles.
export const BASE_INPUT_STYLE: IStyles = {
  appearance: 'none',
  boxSizing: 'border-box',
  color: GREY_B,
  margin: '0',
  minWidth: '0',
  padding: '4px',
  flexGrow: 1,
  ':disabled': {
    opacity: 0.5,
  },
};

export const INPUT_STYLE: IStyles = {
  ...BASE_INPUT_STYLE,
  border: '1px solid #ccc',
  outline: 'none',
  borderRadius: '2px',
  ':focus': {
    borderColor: '#999',
  },
};

// Remove left rounding for inputs with a label so they are snug with the label.
export const INPUT_LABELED_STYLE: IStyles = {
  borderTopLeftRadius: '0px',
  borderBottomLeftRadius: '0px',
};

export interface IInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  getRef?: React.Ref<HTMLInputElement>;
  variant?: InputVariant;
  hasLabel?: boolean;
}

export default function Input(props: IInputProps) {
  const {
    getRef,
    variant = InputVariant.Base,
    hasLabel = false,
    onChange,
    value,
    ...restProps
  } = props;

  const updateValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value;

    if (onChange && eventValue !== value) {
      onChange(eventValue);
    }
  };

  const classes = useClasses({
    input: [
      INPUT_STYLE,
      hasLabel && INPUT_LABELED_STYLE,

      variant === InputVariant.Wide && { width: '100%' },
      variant === InputVariant.Borderless && { border: 'none' },
    ],
  });

  // `restProps` is included first so that it doesn't override any
  // attributes defined here

  // `onBlur` event is fixing a bug with some password managers
  // setting the value directly in JS without firing `onChange`
  // event, in particular iOS version of Chrome
  return (
    <input
      {...restProps}
      ref={getRef}
      className={classes.input}
      onChange={updateValue}
      onBlur={updateValue}
      value={value || ''}
    />
  );
}
